import { useContext, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Popover from "../../../ui/Popover/Popover";
import styles from "./Suggestions.module.scss";
import Button from "../../../ui/Button/Button";
import urls from "../../../routes";
import PopoverContext from "../../../ui/Popover/PopoverContext";

function SuggestionsPopoverContent({
  suggestions,
  onSelect,
}: {
  suggestions: string[];
  onSelect: (suggestion: string) => void;
}) {
  const navigate = useNavigate();
  const { setIsOpen } = useContext(PopoverContext);

  return (
    <div className={styles.popover}>
      <div className={styles.popoverTitle}>All topic suggestions</div>
      <div className={styles.popoverSuggestions}>
        {suggestions.map((suggestion) => (
          <button
            className={styles.suggestion}
            key={suggestion}
            onClick={() => {
              onSelect(suggestion);
              setIsOpen(false);
            }}
          >
            {suggestion}
          </button>
        ))}
      </div>
      <Button
        className={styles.editButton}
        variant="outline"
        size="small"
        onClick={() => navigate(urls.settings.keywords.recommendations)}
      >
        Edit topics
      </Button>
    </div>
  );
}

function Suggestions({
  suggestions,
  onSelect,
}: {
  suggestions: string[];
  onSelect: (suggestion: string) => void;
}) {
  const navigate = useNavigate();
  const [suggestionsRef, setSuggestionsRef] = useState<HTMLDivElement | null>(
    null,
  );
  const [wrapperRef, setWrapperRef] = useState<HTMLDivElement | null>(null);
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const suggestionSpacerRef = useRef<HTMLDivElement>(null);
  const [suggestionsToShow, setSuggestionsToShow] = useState(3);

  useLayoutEffect(() => {
    const calculateSuggestionsToShow = () => {
      if (
        !suggestionsRef ||
        !suggestionSpacerRef.current ||
        !wrapperRef ||
        !containerRef
      ) {
        return;
      }

      const availableWidth = wrapperRef.clientWidth;
      const containerWidth = containerRef.clientWidth;
      const suggestionsWidth = suggestionsRef.clientWidth;

      const suggestionButtons = suggestionSpacerRef.current.querySelectorAll(
        `.${styles.suggestion}`,
      );
      const containerStyle = window.getComputedStyle(suggestionsRef);
      const gap = parseFloat(containerStyle.gap) || 0;

      let totalWidth = containerWidth - suggestionsWidth;
      let count = 0;

      suggestionButtons.forEach((button, index) => {
        const buttonWidth = button.clientWidth;

        totalWidth += buttonWidth;

        if (index > 0) {
          totalWidth += gap;
        }

        if (totalWidth <= availableWidth) {
          count += 1;
        }
      });

      setSuggestionsToShow(Math.max(count, 1));
    };

    const resizeObserver = new ResizeObserver(calculateSuggestionsToShow);

    if (wrapperRef) {
      resizeObserver.observe(wrapperRef);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [suggestionsRef, suggestions.length, containerRef, wrapperRef]);

  if (!suggestions.length) {
    return null;
  }

  return (
    <div className={styles.wrapper} ref={setWrapperRef}>
      <div className={styles.container} ref={setContainerRef}>
        <span className={styles.suggestionsLabel}>Suggestions:</span>
        <div className={styles.suggestions} ref={setSuggestionsRef}>
          {suggestions.slice(0, suggestionsToShow).map((suggestion) => (
            <button
              className={styles.suggestion}
              key={suggestion}
              onClick={() => {
                onSelect(suggestion);
              }}
            >
              {suggestion}
            </button>
          ))}
          <div className={styles.suggestionSpacer} ref={suggestionSpacerRef}>
            {suggestions.slice(0, 3).map((suggestion) => (
              <button
                className={styles.suggestion}
                key={suggestion}
                onClick={() => {
                  onSelect(suggestion);
                }}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
        {suggestions.length > suggestionsToShow ? (
          <Popover
            align="end"
            content={
              <SuggestionsPopoverContent
                suggestions={suggestions}
                onSelect={onSelect}
              />
            }
          >
            <Button
              variant="outline"
              size="small"
              className={styles.viewMoreButton}
            >
              View more
            </Button>
          </Popover>
        ) : (
          <Button
            className={styles.viewMoreButton}
            variant="outline"
            size="small"
            onClick={() => navigate(urls.settings.keywords.recommendations)}
          >
            Edit topics
          </Button>
        )}
      </div>
    </div>
  );
}

export default Suggestions;
